import { useState, useEffect, useCallback } from 'react';
import { FileText, FolderOpen, Download, Eye, ChevronDown, ChevronRight, ArrowLeft, Calendar, Mail } from 'lucide-react';
import Card from '../components/common/Card';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import Button from '../components/common/Button';
import SearchBar from '../components/common/SearchBar';
import Toast from '../components/common/Toast';
import MyDeliveries from '../components/MyDeliveries';
import { getPickupFolders, getPickupFolderFiles, downloadPickupFile } from '../services/pickupServiceSwitch';
import './MyReports.css';

/** Format bytes to human-readable KB/MB */
function formatFileSize(bytes) {
  if (bytes == null) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function MyReports() {
  /* ── Folder list state ── */
  const [folders, setFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(true);
  const [foldersError, setFoldersError] = useState(null);

  /* ── File list state ── */
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [files, setFiles] = useState([]);
  const [archivedFiles, setArchivedFiles] = useState({});
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState(null);

  /* ── Tab state ── */
  const [activeTab, setActiveTab] = useState('pickup-folders');

  /* ── UI state ── */
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expandedDates, setExpandedDates] = useState({});
  const [selectedFileKeys, setSelectedFileKeys] = useState([]);
  const [downloadingKeys, setDownloadingKeys] = useState({});
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [toast, setToast] = useState(null);

  /* ── Fetch folders ── */
  const fetchFolders = useCallback(async () => {
    try {
      setFoldersLoading(true);
      setFoldersError(null);
      const response = await getPickupFolders({ page: 1, limit: 100 });
      setFolders(response.data);
    } catch (err) {
      setFoldersError(err.message || 'Failed to load folders');
    } finally {
      setFoldersLoading(false);
    }
  }, []);

  useEffect(() => { fetchFolders(); }, [fetchFolders]);

  /* ── Default tab: "My Deliveries" if no folders assigned ── */
  useEffect(() => {
    if (!foldersLoading && folders.length === 0) {
      setActiveTab('my-deliveries');
    }
  }, [foldersLoading, folders]);

  /* ── Fetch files for a folder ── */
  const openFolder = async (folder) => {
    try {
      setSelectedFolder(folder);
      setFilesLoading(true);
      setFilesError(null);
      setSearchTerm('');
      setDateFrom('');
      setDateTo('');
      setSelectedFileKeys([]);
      setExpandedDates({});
      const result = await getPickupFolderFiles(folder.id);
      setFiles(result.files || []);
      setArchivedFiles(result.archivedFiles || {});
    } catch (err) {
      setFilesError(err.message || 'Failed to load files');
    } finally {
      setFilesLoading(false);
    }
  };

  const goBack = () => {
    setSelectedFolder(null);
    setFiles([]);
    setArchivedFiles({});
    setSearchTerm('');
    setDateFrom('');
    setDateTo('');
    setSelectedFileKeys([]);
  };

  /* ── Filtering logic ── */
  const matchesSearch = (file) => {
    if (!searchTerm) return true;
    return file.filename.toLowerCase().includes(searchTerm.toLowerCase());
  };

  const matchesDateRange = (file) => {
    if (!dateFrom && !dateTo) return true;
    const fileDate = file.lastModified ? new Date(file.lastModified) : null;
    if (!fileDate) return true;
    if (dateFrom && fileDate < new Date(dateFrom)) return false;
    if (dateTo) {
      const toEnd = new Date(dateTo);
      toEnd.setHours(23, 59, 59, 999);
      if (fileDate > toEnd) return false;
    }
    return true;
  };

  const filterFile = (file) => matchesSearch(file) && matchesDateRange(file);

  const filteredFiles = files.filter(filterFile);

  const filteredArchivedFiles = {};
  for (const [date, dateFiles] of Object.entries(archivedFiles)) {
    const filtered = dateFiles.filter(filterFile);
    if (filtered.length > 0) filteredArchivedFiles[date] = filtered;
  }

  /* ── All visible file keys (for select-all) ── */
  const allVisibleKeys = [
    ...filteredFiles.map(f => f.key),
    ...Object.values(filteredArchivedFiles).flat().map(f => f.key)
  ];

  /* ── Selection helpers ── */
  const toggleFileSelection = (key) => {
    setSelectedFileKeys(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const toggleSelectAll = () => {
    if (selectedFileKeys.length === allVisibleKeys.length) {
      setSelectedFileKeys([]);
    } else {
      setSelectedFileKeys([...allVisibleKeys]);
    }
  };

  /* ── Download helpers ── */
  const handleDownload = async (folderId, fileKey) => {
    try {
      setDownloadingKeys(prev => ({ ...prev, [fileKey]: true }));
      const result = await downloadPickupFile(folderId, fileKey, 'download');
      if (result.downloadUrl) {
        const a = document.createElement('a');
        a.href = result.downloadUrl;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (err) {
      setToast({ message: err.message || 'Download failed', type: 'error' });
    } finally {
      setDownloadingKeys(prev => ({ ...prev, [fileKey]: false }));
    }
  };

  const handleView = async (folderId, fileKey) => {
    try {
      setDownloadingKeys(prev => ({ ...prev, [fileKey]: true }));
      const result = await downloadPickupFile(folderId, fileKey, 'view');
      if (result.downloadUrl) {
        window.open(result.downloadUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      setToast({ message: err.message || 'Failed to open file', type: 'error' });
    } finally {
      setDownloadingKeys(prev => ({ ...prev, [fileKey]: false }));
    }
  };

  const handleDownloadSelected = async () => {
    if (selectedFileKeys.length === 0) return;
    setDownloadingAll(true);
    for (const key of selectedFileKeys) {
      await handleDownload(selectedFolder.id, key);
    }
    setDownloadingAll(false);
    setToast({ message: `Downloaded ${selectedFileKeys.length} file(s)`, type: 'success' });
  };

  /* ── Toggle archive date group ── */
  const toggleDate = (date) => {
    setExpandedDates(prev => ({ ...prev, [date]: !prev[date] }));
  };

  /* ── File row renderer ── */
  const renderFileRow = (file) => {
    const isDownloading = !!downloadingKeys[file.key];
    return (
      <tr key={file.key}>
        <td className="checkbox-cell">
          <input
            type="checkbox"
            checked={selectedFileKeys.includes(file.key)}
            onChange={() => toggleFileSelection(file.key)}
            aria-label={`Select ${file.filename}`}
          />
        </td>
        <td>
          <div className="file-name-cell">
            <FileText size={16} />
            <span>{file.filename}</span>
          </div>
        </td>
        <td>{formatFileSize(file.size)}</td>
        <td>{file.lastModified ? new Date(file.lastModified).toLocaleString() : '-'}</td>
        <td>
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            <button
              className="download-btn"
              onClick={() => handleView(selectedFolder.id, file.key)}
              disabled={isDownloading}
              title="View file in browser"
            >
              <Eye size={14} />
              View
            </button>
            <button
              className="download-btn"
              onClick={() => handleDownload(selectedFolder.id, file.key)}
              disabled={isDownloading}
              title="Download file"
            >
              {isDownloading ? <span className="btn-spinner" /> : <Download size={14} />}
              {isDownloading ? 'Loading…' : 'Download'}
            </button>
          </div>
        </td>
      </tr>
    );
  };

  /* ── RENDER: File detail view (inside a folder) ── */
  if (selectedFolder) {
    const archiveDateKeys = Object.keys(filteredArchivedFiles).sort().reverse();

    return (
      <div className="my-reports-page">
        <div className="page-header">
          <div className="header-content">
            <div className="header-text">
              <h1 className="page-title"><FileText size={32} /> My Reports</h1>
              <p className="page-subtitle">View and download your assigned pickup folder files</p>
            </div>
          </div>
        </div>

        <button className="my-reports-back-btn" onClick={goBack}>
          <ArrowLeft size={16} /> Back to folders
        </button>

        <Card>
          <h2 style={{ margin: '0 0 var(--spacing-4) 0', fontSize: 'var(--font-size-lg)', color: 'var(--text-primary)' }}>
            <FolderOpen size={20} style={{ verticalAlign: 'middle', marginRight: '0.5rem', color: 'var(--color-primary-400)' }} />
            {selectedFolder.name}
          </h2>

          {/* Controls: search + date filter */}
          <div className="my-reports-controls">
            <SearchBar
              value={searchTerm}
              onChange={(e) => setSearchTerm(typeof e === 'string' ? e : e.target.value)}
              placeholder="Search files by name..."
            />
            <div className="my-reports-date-filter">
              <Calendar size={16} />
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} title="From date" />
              <span>–</span>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} title="To date" />
            </div>
          </div>

          {/* Download selected bar */}
          {allVisibleKeys.length > 0 && (
            <div className="my-reports-download-bar">
              <input
                type="checkbox"
                checked={selectedFileKeys.length === allVisibleKeys.length && allVisibleKeys.length > 0}
                onChange={toggleSelectAll}
                aria-label="Select all files"
              />
              <span className="selected-count">{selectedFileKeys.length} selected</span>
              <Button
                variant="primary"
                size="sm"
                icon={<Download size={16} />}
                disabled={selectedFileKeys.length === 0 || downloadingAll}
                loading={downloadingAll}
                onClick={handleDownloadSelected}
              >
                Download Selected
              </Button>
            </div>
          )}

          {filesLoading && <LoadingSpinner />}
          {filesError && <ErrorMessage message={filesError} onRetry={() => openFolder(selectedFolder)} />}

          {!filesLoading && !filesError && (
            <>
              {/* Current files */}
              {filteredFiles.length > 0 ? (
                <table className="my-reports-file-table">
                  <thead>
                    <tr>
                      <th className="checkbox-cell"></th>
                      <th>Filename</th>
                      <th>Size</th>
                      <th>Last Modified</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFiles.map(renderFileRow)}
                  </tbody>
                </table>
              ) : (
                files.length === 0 && Object.keys(archivedFiles).length === 0 && (
                  <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem 0' }}>
                    No files in this folder yet
                  </p>
                )
              )}

              {/* Archived files */}
              {archiveDateKeys.length > 0 && (
                <div className="my-reports-archive-section">
                  <h3>Archived Files</h3>
                  {archiveDateKeys.map(date => (
                    <div key={date} className="archive-date-group">
                      <button className="archive-date-toggle" onClick={() => toggleDate(date)}>
                        {expandedDates[date] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        {date}
                        <span className="date-file-count">{filteredArchivedFiles[date].length} file(s)</span>
                      </button>
                      {expandedDates[date] && (
                        <div className="archive-date-files">
                          <table className="my-reports-file-table">
                            <thead>
                              <tr>
                                <th className="checkbox-cell"></th>
                                <th>Filename</th>
                                <th>Size</th>
                                <th>Last Modified</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredArchivedFiles[date].map(renderFileRow)}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </Card>

        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} duration={5000} />}
      </div>
    );
  }

  /* ── RENDER: Main view with tabs ── */
  return (
    <div className="my-reports-page">
      <div className="page-header">
        <div className="header-content">
          <div className="header-text">
            <h1 className="page-title"><FileText size={32} /> My Reports</h1>
            <p className="page-subtitle">View and download your reports and pickup folder files</p>
          </div>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="my-reports-tabs">
        <button
          className={`my-reports-tab${activeTab === 'pickup-folders' ? ' active' : ''}`}
          onClick={() => setActiveTab('pickup-folders')}
        >
          <FolderOpen size={16} />
          Pickup Folders
        </button>
        <button
          className={`my-reports-tab${activeTab === 'my-deliveries' ? ' active' : ''}`}
          onClick={() => setActiveTab('my-deliveries')}
        >
          <Mail size={16} />
          My Deliveries
        </button>
      </div>

      {/* Tab content */}
      {activeTab === 'pickup-folders' && (
        <>
          {foldersLoading && <LoadingSpinner />}
          {foldersError && <ErrorMessage message={foldersError} onRetry={fetchFolders} />}
          {!foldersLoading && !foldersError && folders.length === 0 && (
            <Card>
              <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem 0' }}>
                No folders assigned to you yet. Contact your manager to get access.
              </p>
            </Card>
          )}
          {!foldersLoading && !foldersError && folders.length > 0 && (
            <div className="my-reports-folders">
              {folders.map(folder => (
                <Card
                  key={folder.id}
                  className="my-reports-folder-card"
                  hoverable
                  clickable
                  onClick={() => openFolder(folder)}
                >
                  <FolderOpen size={28} className="folder-card-icon" />
                  <h3 className="folder-card-name">{folder.name}</h3>
                  {folder.description && <p className="folder-card-desc">{folder.description}</p>}
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === 'my-deliveries' && (
        <Card>
          <MyDeliveries />
        </Card>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} duration={5000} />}
    </div>
  );
}
